// ═══════════════════════════════════════════════════════════════════════════
// Extractor de campos de la Constancia de Situación Fiscal — lote 2.5
//
// Reemplaza las regex de parseCSF que corrompieron los domicilios de 9
// clientes reales. La causa: pdf.js junta varios pares etiqueta:valor en una
// sola línea visual —
//
//   "Nombre de Vialidad: PROLONGACION PASEO DE LOS HEROES Número Exterior: 13990"
//
// — y una regex que captura "hasta el fin de línea" arrastra las etiquetas
// vecinas. La regla de este extractor: UN VALOR TERMINA DONDE EMPIEZA LA
// SIGUIENTE ETIQUETA, nunca al final de la línea.
//
// Función pura. Probada contra las dos CSF reales (persona moral y física).
// ═══════════════════════════════════════════════════════════════════════════

import { SAT_REGIMEN_FISCAL } from "./catalogos-sat.js";

// Todas las etiquetas que el SAT usa en el bloque de identificación y
// domicilio. Sirven como TIJERAS: cortan el valor de la etiqueta anterior.
const ETIQUETAS = [
  "RFC", "CURP", "Nombre \\(s\\)", "Nombre\\(s\\)",
  "Primer Apellido", "Segundo Apellido",
  "Denominaci[óo]n/Raz[óo]n Social", "Denominaci[óo]n o Raz[óo]n Social",
  "R[ée]gimen Capital", "Nombre Comercial",
  "Fecha inicio de operaciones", "Estatus en el padr[óo]n",
  "Fecha de [úu]ltimo cambio de estado",
  "C[óo]digo Postal", "Tipo de Vialidad", "Nombre de Vialidad",
  "N[úu]mero Exterior", "N[úu]mero Interior",
  "Nombre de la Colonia", "Nombre de la Localidad",
  "Nombre del Municipio o Demarcaci[óo]n Territorial",
  "Nombre de la Entidad Federativa",
  "Entre Calle", "Y Calle",
  "Correo Electr[óo]nico", "Tel\\. Fijo Lada", "N[úu]mero",
  "AL", // "Lugar y Fecha de Emisión"… encabezados que a veces se cuelan
];

const RE_ETIQUETA = new RegExp(`(${ETIQUETAS.join("|")})\\s*:`, "gi");
const RE_RFC  = /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/;
const RE_CURP = /^[A-Z]{4}\d{6}[HM][A-Z]{5}[A-Z0-9]\d$/;

/**
 * Parte una línea visual en sus pares etiqueta→valor.
 * "Código Postal: 22104 Tipo de Vialidad: CALLE"
 *   → [["codigo postal","22104"], ["tipo de vialidad","CALLE"]]
 */
export function segmentarLinea(linea) {
  const cortes = [...linea.matchAll(RE_ETIQUETA)];
  const pares = [];
  for (let i = 0; i < cortes.length; i++) {
    const etiqueta = normalizar(cortes[i][1]);
    const desde = cortes[i].index + cortes[i][0].length;
    const hasta = i + 1 < cortes.length ? cortes[i + 1].index : linea.length;
    pares.push([etiqueta, linea.slice(desde, hasta).trim()]);
  }
  return pares;
}

function normalizar(s) {
  return s.toLowerCase()
    .replace(/[áà]/g, "a").replace(/[éè]/g, "e").replace(/[íì]/g, "i")
    .replace(/[óò]/g, "o").replace(/[úù]/g, "u")
    .replace(/\s+/g, " ").replace(/[():.]/g, "").trim();
}

/**
 * Extrae los campos del cliente a partir de las líneas visuales de la CSF.
 * Mantiene el MISMO contrato de resultado que parseCSF (ClientManager lo
 * consume tal cual: rfc, curp, legalName, taxSystem, domicilioFiscalZip,
 * street, colonia, city, state…).
 */
export function extraerCamposDeLineas(lineas) {
  const r = {
    rfc: "", curp: "", legalName: "", firstName: "", lastName1: "", lastName2: "",
    taxSystem: "", taxSystemLabel: "",
    domicilioFiscalZip: "", street: "", exterior: "", interior: "",
    colonia: "", city: "", state: "", cp: "",
    regimenes: [],                     // NUEVO: todos los regímenes del contribuyente
    _confidence: {}, _warnings: [],
  };

  // 1. Mapa etiqueta → primer valor no vacío
  const valores = new Map();
  for (const linea of lineas) {
    for (const [etiqueta, valor] of segmentarLinea(linea)) {
      if (valor && !valores.has(etiqueta)) valores.set(etiqueta, valor);
    }
  }
  const v = (...claves) => {
    for (const c of claves) if (valores.has(c)) return valores.get(c);
    return "";
  };

  // 2. Identidad
  const rfcCrudo = v("rfc");
  if (RE_RFC.test(rfcCrudo)) { r.rfc = rfcCrudo; r._confidence.rfc = "etiqueta"; }
  else {
    // Rescate: el RFC a veces queda como línea suelta (visto en CSF real de
    // persona física, donde "RFC:" y su valor caen en coordenadas distintas).
    const suelto = lineas.find(l => RE_RFC.test(l.trim()));
    if (suelto) { r.rfc = suelto.trim(); r._confidence.rfc = "linea-suelta"; }
  }

  const curpCrudo = v("curp");
  if (RE_CURP.test(curpCrudo)) { r.curp = curpCrudo; r._confidence.curp = "etiqueta"; }

  r.legalName  = v("denominacion/razon social", "denominacion o razon social");
  r.firstName  = v("nombre s", "nombres");
  r.lastName1  = v("primer apellido");
  r.lastName2  = v("segundo apellido");
  if (!r.legalName && r.firstName) {
    r.legalName = [r.firstName, r.lastName1, r.lastName2].filter(Boolean).join(" ");
    r._confidence.legalName = "armado-de-partes";
  }

  // 3. Domicilio — cada campo llega ya cortado por la siguiente etiqueta
  r.domicilioFiscalZip = (v("codigo postal").match(/\d{4,5}/) || [""])[0];
  r.cp = r.domicilioFiscalZip;
  const vialidad  = v("nombre de vialidad");
  r.exterior      = v("numero exterior");
  r.interior      = v("numero interior");
  r.colonia       = v("nombre de la colonia");
  const localidad = v("nombre de la localidad");
  const municipio = v("nombre del municipio o demarcacion territorial");
  r.city  = localidad || municipio;   // la localidad puede venir vacía (visto)
  r.state = v("nombre de la entidad federativa");

  r.street = [vialidad, r.exterior].filter(Boolean).join(" ");
  if (r.interior) r.street += " Int. " + r.interior;

  // 4. Regímenes — TODOS (una persona física puede tener varios; visto: 4)
  const texto = lineas.join("\n");
  for (const reg of SAT_REGIMEN_FISCAL) {
    // el catálogo dice "Personas Físicas con Actividades Empresariales…";
    // la CSF dice "Régimen de las Personas Físicas con…" — contener basta
    if (texto.includes(reg.label) ||
        texto.toLowerCase().includes(reg.label.toLowerCase())) {
      r.regimenes.push({ code: reg.code, label: reg.label });
    }
  }
  if (r.regimenes.length) {
    r.taxSystem = r.regimenes[0].code;
    r.taxSystemLabel = r.regimenes[0].label;
    if (r.regimenes.length > 1) {
      r._warnings.push({ level: "info",
        message: `El contribuyente tiene ${r.regimenes.length} regímenes; se propuso ${r.taxSystemLabel}. Verifica cuál aplica a la facturación.` });
    }
  }

  // 5. Avisos de calidad
  if (!r.rfc && !r.curp)
    r._warnings.push({ level: "critical", message: "No se encontró RFC ni CURP." });
  if (!r.domicilioFiscalZip)
    r._warnings.push({ level: "critical", message: "No se encontró el código postal fiscal." });
  if (!r.legalName)
    r._warnings.push({ level: "warning", message: "No se encontró razón social ni nombre." });

  return r;
}
